import React from 'react';
import { ShieldCheck, BadgeCheck } from 'lucide-react';

/**
 * EndorsementBadge — small pill that surfaces an item's trust level.
 *
 * Three states:
 *   • certified  — independently verified by the data/BI team (high trust)
 *   • promoted   — owners think it's good for sharing (lower trust)
 *   • none / undefined — render nothing (don't pollute lists with empty badges)
 *
 * Used in dashboard / report / model / visual list rows AND in detail
 * headers + AI Copilot "source: …" footnotes. Keeps the badge consistent
 * everywhere so users learn the visual cue once.
 *
 * Props:
 *   endorsement: { level, set_by_email, set_at, note }  // shape from /governance/endorse
 *   size: 'sm' | 'md'   — md is for detail headers, sm for list rows
 *   showLabel: bool     — false to render just the icon (mobile / dense rows)
 */
export function EndorsementBadge({ endorsement, size = 'sm', showLabel = true }) {
  const level = endorsement?.level || 'none';
  if (level === 'none') return null;

  const isCertified = level === 'certified';
  const Icon = isCertified ? BadgeCheck : ShieldCheck;
  const cls = isCertified
    ? 'bg-emerald-500/15 text-emerald-300 border-emerald-500/30'
    : 'bg-amber-500/15 text-amber-300 border-amber-500/30';
  const sizeCls = size === 'md'
    ? 'px-2.5 py-1 text-[11px] gap-1.5'
    : 'px-1.5 py-0.5 text-[9px] gap-1';
  const iconSize = size === 'md' ? 'w-3.5 h-3.5' : 'w-2.5 h-2.5';

  const tooltip = (() => {
    const label = isCertified ? 'Certified' : 'Promoted';
    const by = endorsement.set_by_email ? ` · by ${endorsement.set_by_email}` : '';
    const note = endorsement.note ? ` — “${endorsement.note}”` : '';
    return `${label}${by}${note}`;
  })();

  return (
    <span
      className={`inline-flex items-center font-semibold rounded-full border uppercase tracking-wider ${cls} ${sizeCls}`}
      title={tooltip}
      data-testid={`endorsement-badge-${level}`}
    >
      <Icon className={iconSize} strokeWidth={2.5} />
      {showLabel && <span>{isCertified ? 'Certified' : 'Promoted'}</span>}
    </span>
  );
}

export default EndorsementBadge;
